import {useState} from 'react';
import {Plus} from 'lucide-react';
import {
  Button,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {useCreateJob, useClassificationConfig} from '@/api/hooks';
import {isPolicyViolationType, isVisibleForFacet} from '@/lib/classificationType';

export function JobSubmitForm() {
  const createJob = useCreateJob();
  const {data: classifications} = useClassificationConfig();
  const verticals = (classifications ?? []).filter(
    (c) => isPolicyViolationType(c.type) && isVisibleForFacet(c)
  );
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({search_term: '', affiliate: '', platform: 'youtube'});
  const [targetPolicyViolations, setTargetPolicyViolations] = useState<string[]>([]);

  const submit = () => {
    if (form.search_term === '' || form.affiliate === '' || targetPolicyViolations.length === 0) {
      return;
    }

    // Affiliate is collected for parity but the API assigns it server-side (default_affiliate).
    createJob.mutate(
      {
        search_term: form.search_term,
        platform: form.platform,
        target_policy_violations: targetPolicyViolations
      },
      {
        onSuccess() {
          setOpen(false);
        }
      }
    );
  };

  const disabled =
    createJob.isPending ||
    form.search_term === '' ||
    form.affiliate === '' ||
    targetPolicyViolations.length === 0;

  return (
    <>
      <Button
        size="sm"
        type="button"
        onClick={() => {
          setOpen(true);
        }}>
        <Plus />
        New Job
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>YouTube Search Job</SheetTitle>
            <SheetDescription>
              Run a YouTube search via the Scrapy cluster and collect results.
            </SheetDescription>
          </SheetHeader>
          <FieldGroup className="px-4">
            <Field>
              <FieldLabel htmlFor="job-search-term">Search query</FieldLabel>
              <Input
                id="job-search-term"
                placeholder="e.g. climate change misinformation"
                value={form.search_term}
                onChange={(event) => {
                  setForm((f) => ({...f, search_term: event.target.value}));
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="job-affiliate">Affiliate</FieldLabel>
              <Input
                id="job-affiliate"
                placeholder="CustomerName"
                value={form.affiliate}
                onChange={(event) => {
                  setForm((f) => ({...f, affiliate: event.target.value}));
                }}
              />
            </Field>
            <Field>
              <FieldLabel>Target policy violations</FieldLabel>
              <ToggleGroup
                multiple
                variant="outline"
                size="sm"
                className="flex-wrap"
                value={targetPolicyViolations}
                onValueChange={(next) => {
                  setTargetPolicyViolations(next);
                }}>
                {verticals.map((v) => (
                  <ToggleGroupItem key={v.id} value={v.name}>
                    {v.displayName}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </Field>
          </FieldGroup>
          <SheetFooter>
            <Button className="w-full" disabled={disabled} onClick={submit}>
              {createJob.isPending ? <Spinner /> : null}
              {createJob.isPending ? 'Submitting…' : 'Run Search'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
